import { LoaderCircle, Square, Volume2 } from "lucide-react";
import { useEffect, useId, useState } from "react";
import type { WordPronunciation as WordPronunciationData } from "@preptrack/contracts";
import {
  getBrowserAudioController,
  type AudioPlaybackState,
  type PronunciationAccent,
} from "@/lib/audio-playback";

interface WordPronunciationProps {
  wordId: string;
  headword: WordPronunciationData["headword"];
  phoneticUk?: WordPronunciationData["phoneticUk"] | null;
  phoneticUs?: WordPronunciationData["phoneticUs"] | null;
  compact?: boolean;
  className?: string;
}

const accentLabel: Record<PronunciationAccent, string> = { uk: "英式", us: "美式" };

export function WordPronunciation({
  wordId,
  headword,
  phoneticUk,
  phoneticUs,
  compact = false,
  className = "",
}: WordPronunciationProps) {
  const [playback, setPlayback] = useState<{ accent: PronunciationAccent; state: Exclude<AudioPlaybackState, "idle"> } | null>(null);
  const [error, setError] = useState<PronunciationAccent | null>(null);
  const instanceKey = useId();

  useEffect(() => () => {
    const controller = getBrowserAudioController();
    controller.stop(`${instanceKey}:uk`);
    controller.stop(`${instanceKey}:us`);
  }, [instanceKey]);

  const play = (accent: PronunciationAccent) => {
    setError(null);
    const key = `${instanceKey}:${accent}`;
    getBrowserAudioController().toggle({
      key,
      path: `/words/${encodeURIComponent(wordId)}/pronunciation/${accent}`,
      onState: (state) => setPlayback(state === "idle" ? null : { accent, state }),
      onError: () => setError(accent),
    });
  };

  const row = (accent: PronunciationAccent, phonetic: string | null | undefined) => {
    const active = playback?.accent === accent;
    const loading = active && playback.state === "loading";
    const failed = error === accent;
    const label = accent === "uk" ? "UK" : "US";
    return (
      <span className="word-pronunciation__accent" key={accent}>
        <span className="word-pronunciation__ipa"><b>{label}</b> /{phonetic || "—"}/</span>
        <button
          type="button"
          className={`word-pronunciation__play${active ? " word-pronunciation__play--active" : ""}${failed ? " word-pronunciation__play--error" : ""}`}
          onClick={() => play(accent)}
          disabled={!phonetic}
          aria-label={active ? `停止播放 ${headword} 的${accentLabel[accent]}发音` : `播放 ${headword} 的${accentLabel[accent]}发音`}
          aria-pressed={active}
          title={active ? "停止播放" : failed ? "发音暂时不可用，点击重试" : `播放${accentLabel[accent]}发音`}
        >
          {loading ? <LoaderCircle className="word-pronunciation__spinner" /> : active ? <Square /> : <Volume2 />}
        </button>
      </span>
    );
  };

  return (
    <span className={`word-pronunciation${compact ? " word-pronunciation--compact" : ""}${className ? ` ${className}` : ""}`}>
      {row("uk", phoneticUk)}
      {row("us", phoneticUs)}
      <span className="sr-only" aria-live="polite">{error ? `${accentLabel[error]}发音暂时不可用` : playback ? `${playback.state === "loading" ? "正在加载" : "正在播放"}${accentLabel[playback.accent]}发音` : ""}</span>
    </span>
  );
}
